// app/frontend/pages/Admin/LearningTracks/Index.tsx
import { useState } from "react";
import {
  Box,
  Stack,
  Group,
  Table,
  Badge,
  ActionIcon,
  Menu,
  TextInput,
  Pagination,
} from "@mantine/core";
import {
  IconSearch,
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconPlus,
} from "@tabler/icons-react";
import { Link, router } from "@inertiajs/react";
import { L2KH2, L2KText, L2KCaption } from "../../../components/ui/Typography";
import { L2KButton } from "../../../components/ui/Forms";
import { l2kNotify } from "../../../components/ui/Feedback";
import AdminLayout from "../../../components/layouts/AdminLayout";
import { SEO } from "../../../components/common/SEO";

interface LearningTrack {
  id: number;
  name: string;
  description: string;
  videos_count: number;
  created_at: string;
  updated_at: string;
}

interface Props {
  tracks: LearningTrack[];
  filters: {
    search: string | null;
  };
  pagination: {
    page: number;
    per_page: number;
    total_count: number;
    total_pages: number;
  };
  stats: {
    total: number;
    total_videos: number;
  };
}

export default function LearningTracksIndex({
  tracks,
  filters,
  pagination,
  stats,
}: Props) {
  const [search, setSearch] = useState(filters.search || "");

  const handleSearch = () => {
    router.get("/admin/learning_tracks", { ...filters, search, page: 1 }, { preserveState: true });
  };

  const handleDelete = async (track: LearningTrack) => {
    if (!confirm(`Delete "${track.name}"? This will remove all video assignments.`)) return;

    try {
      const response = await fetch(`/admin/learning_tracks/${track.id}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "",
          "Accept": "application/json",
        },
      });
      if (response.ok) {
        l2kNotify.success("Deleted", "Learning track deleted successfully");
        router.reload();
      } else {
        l2kNotify.error("Error", "Failed to delete learning track");
      }
    } catch (error) {
      l2kNotify.error("Error", "Failed to delete learning track");
    }
  };

  return (
    <>
      <SEO title="Learning Tracks Admin - Learntokiz" />
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <Box>
            <L2KH2 mb="xs">Learning Tracks</L2KH2>
            <L2KText variant="secondary">
              {stats.total} tracks with {stats.total_videos} video assignments
            </L2KText>
          </Box>
          <Link href="/admin/learning_tracks/new">
            <L2KButton leftSection={<IconPlus size={16} />}>
              New Track
            </L2KButton>
          </Link>
        </Group>

        {/* Search */}
        <Group gap="sm">
          <TextInput
            placeholder="Search tracks..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            leftSection={<IconSearch size={16} />}
            style={{ flex: 1, maxWidth: 300 }}
            styles={{
              input: {
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                borderColor: "rgba(255, 255, 255, 0.1)",
                color: "#E0E0E0",
              },
            }}
          />
        </Group>

        {/* Table */}
        <Box style={{ overflowX: "auto" }}>
          <Table
            horizontalSpacing="md"
            verticalSpacing="sm"
            styles={{
              table: { backgroundColor: "transparent" },
              th: { color: "#888", borderColor: "rgba(255,255,255,0.1)" },
              td: { borderColor: "rgba(255,255,255,0.05)" },
            }}
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th>Videos</Table.Th>
                <Table.Th>Created</Table.Th>
                <Table.Th w={60}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {tracks.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <L2KText variant="tertiary" ta="center" py="xl">
                      No learning tracks found
                    </L2KText>
                  </Table.Td>
                </Table.Tr>
              ) : (
                tracks.map((track) => (
                  <Table.Tr key={track.id}>
                    <Table.Td>
                      <L2KText size="sm" fw={500}>
                        {track.name}
                      </L2KText>
                    </Table.Td>
                    <Table.Td>
                      <L2KText size="sm" variant="secondary" lineClamp={1}>
                        {track.description}
                      </L2KText>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" color="orange" variant="light">
                        {track.videos_count} videos
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <L2KCaption variant="tertiary">{track.created_at}</L2KCaption>
                    </Table.Td>
                    <Table.Td>
                      <Menu position="bottom-end" shadow="md">
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray">
                            <IconDotsVertical size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown
                          style={{
                            backgroundColor: "rgba(23, 23, 23, 0.98)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                          }}
                        >
                          <Menu.Item
                            leftSection={<IconEdit size={14} />}
                            component={Link}
                            href={`/admin/learning_tracks/${track.id}/edit`}
                          >
                            Edit
                          </Menu.Item>
                          <Menu.Divider />
                          <Menu.Item
                            leftSection={<IconTrash size={14} />}
                            color="red"
                            onClick={() => handleDelete(track)}
                          >
                            Delete
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </Box>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <Group justify="center" mt="lg">
            <Pagination
              total={pagination.total_pages}
              value={pagination.page}
              onChange={(page) => router.get("/admin/learning_tracks", { ...filters, page }, { preserveState: true })}
              color="orange"
            />
          </Group>
        )}
      </Stack>
    </>
  );
}

LearningTracksIndex.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>;
